import { useEffect, useState } from 'react';
import { CONTEST_MATURATION_STEPS } from '@isbd/shared';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthProvider';
import { useToast } from '@/features/ui/toast/useToast';

export type MaturationDecision = 'pending' | 'in_review' | 'to_correct' | 'approved' | 'rejected';

export interface MaturationStepRow {
  id?: string;
  etape: string;
  decision: MaturationDecision;
  admin_comment: string;
  updated_at?: string | null;
}

const DECISION_LABELS: Record<MaturationDecision, string> = {
  pending: 'En attente',
  in_review: 'En évaluation',
  to_correct: 'À corriger',
  approved: 'Validé',
  rejected: 'Rejeté',
};

const ALL_DECISIONS: MaturationDecision[] = ['pending', 'in_review', 'to_correct', 'approved', 'rejected'];

/** Assure les 6 lignes d’étapes pour une inscription. */
export async function ensureMaturationSteps(registrationId: string): Promise<MaturationStepRow[]> {
  const { data: existing, error: readErr } = await supabase
    .from('contest_maturation_steps')
    .select('id, step, decision, admin_comment, updated_at')
    .eq('registration_id', registrationId);
  if (readErr) throw readErr;

  const byStep = Object.fromEntries((existing ?? []).map((r) => [r.step, r]));
  const missing = CONTEST_MATURATION_STEPS.filter((s) => !byStep[s.slug]).map((s) => ({
    registration_id: registrationId,
    step: s.slug,
    decision: 'pending' as const,
    admin_comment: '',
  }));

  if (missing.length) {
    const { error: insertErr } = await supabase.from('contest_maturation_steps').insert(missing);
    if (insertErr) throw insertErr;
  }

  const { data: rows, error } = await supabase
    .from('contest_maturation_steps')
    .select('id, step, decision, admin_comment, updated_at')
    .eq('registration_id', registrationId);
  if (error) throw error;

  return CONTEST_MATURATION_STEPS.map((s) => {
    const found = (rows ?? []).find((r) => r.step === s.slug);
    return {
      id: found?.id,
      etape: s.slug,
      decision: (found?.decision ?? 'pending') as MaturationDecision,
      admin_comment: found?.admin_comment ?? '',
      updated_at: found?.updated_at ?? null,
    };
  });
}

interface AdminProps {
  registrationId: string;
  readOnly?: boolean;
}

export function ContestMaturationPanel({ registrationId, readOnly = false }: AdminProps) {
  const { profile, isStaff } = useAuth();
  const toast = useToast();
  const [steps, setSteps] = useState<MaturationStepRow[]>([]);
  const [saving, setSaving] = useState<string | null>(null);

  async function reload() {
    try {
      setSteps(await ensureMaturationSteps(registrationId));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Chargement parcours impossible');
    }
  }

  useEffect(() => {
    void reload();
  }, [registrationId]);

  const activeIndex = (() => {
    const rejected = steps.findIndex((s) => s.decision === 'rejected');
    if (rejected >= 0) return rejected;
    const firstOpen = steps.findIndex((s) => s.decision !== 'approved');
    return firstOpen === -1 ? Math.max(steps.length - 1, 0) : firstOpen;
  })();

  async function saveStep(stepSlug: string, decision: MaturationDecision, comment: string) {
    if (!isStaff || readOnly) return;
    setSaving(stepSlug);
    const { error: err } = await supabase.from('contest_maturation_steps').upsert(
      {
        registration_id: registrationId,
        step: stepSlug,
        decision,
        admin_comment: comment,
        updated_by: profile?.id ?? null,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'registration_id,step' },
    );
    setSaving(null);
    if (err) toast.error(err.message);
    else {
      toast.success('Étape mise à jour.');
      await reload();
    }
  }

  return (
    <div>
      <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '0.75rem' }}>
        {CONTEST_MATURATION_STEPS.map((meta, idx) => {
          const row = steps.find((s) => s.etape === meta.slug) ?? {
            etape: meta.slug,
            decision: 'pending' as MaturationDecision,
            admin_comment: '',
          };
          const isActive = idx === activeIndex;
          return (
            <li
              key={meta.slug}
              className="panel"
              style={{
                boxShadow: isActive ? 'var(--shadow)' : 'none',
                outline: isActive ? '2px solid var(--accent)' : undefined,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
                <strong>
                  {meta.order}. {meta.label}
                </strong>
                <span className={`chip ${row.decision === 'approved' ? 'ok' : row.decision === 'rejected' ? 'warn' : ''}`}>
                  {DECISION_LABELS[row.decision]}
                </span>
              </div>
              {readOnly ? (
                <p className="muted">{row.admin_comment || 'Pas encore de commentaire.'}</p>
              ) : (
                <StepEditor
                  decision={row.decision}
                  comment={row.admin_comment}
                  busy={saving === meta.slug}
                  onSave={(d, c) => void saveStep(meta.slug, d, c)}
                />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function StepEditor({
  decision,
  comment,
  busy,
  onSave,
}: {
  decision: MaturationDecision;
  comment: string;
  busy: boolean;
  onSave: (d: MaturationDecision, c: string) => void;
}) {
  const [d, setD] = useState(decision);
  const [c, setC] = useState(comment);
  useEffect(() => {
    setD(decision);
    setC(comment);
  }, [decision, comment]);

  return (
    <div style={{ marginTop: '0.65rem' }}>
      <div className="field">
        <label>Décision</label>
        <select value={d} onChange={(e) => setD(e.target.value as MaturationDecision)}>
          {ALL_DECISIONS.map((x) => (
            <option key={x} value={x}>
              {DECISION_LABELS[x]}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label>Commentaire</label>
        <textarea value={c} onChange={(e) => setC(e.target.value)} />
      </div>
      <button type="button" className="btn btn-primary" disabled={busy} onClick={() => onSave(d, c)}>
        {busy ? 'Enregistrement…' : 'Enregistrer l’étape'}
      </button>
    </div>
  );
}
